/**
 * Fila de envio otimista persistida (RF-UX-051/053/057/058; EC-UX-001/002).
 * Sobrevive a reload/fechar o app e e drenada ao reconectar, preservando a
 * ordem. A dedupe real e do servidor via `clientMessageId`.
 *
 * Mídia (docs/06): o item carrega `mediaId` quando o upload já concluiu; se
 * o blob ainda está pendente (offline), a drenagem faz upload antes do envio
 * usando o binário guardado em IndexedDB (`saveMediaBlob`).
 */
import { create } from "zustand";
import { enqueue, readMediaBlob, readQueue, removeFromQueue, updateQueued, type QueuedMessage } from "./db";
import { api } from "./api";
import { ZapirApiError, asApiError } from "./errors";
import { toast } from "./store";
import {
  releaseLocalMedia,
  takeLocalBlob,
  uploadMedia,
  type UploadProgress,
} from "./media";

export type OutboxStatus = "pending" | "failed" | "sending";

export interface OutboxItem extends QueuedMessage {
  status: OutboxStatus;
  errorCode?: string;
  /** Progresso do upload em andamento (bolha otimista). */
  progress?: UploadProgress | null;
}

interface OutboxState {
  items: OutboxItem[];
  hydratedFor: string | null;
  hydrate: (tenantId: string) => Promise<void>;
  add: (tenantId: string, item: QueuedMessage) => Promise<void>;
  remove: (tenantId: string, clientMessageId: string) => Promise<void>;
  setStatus: (clientMessageId: string, status: OutboxStatus, errorCode?: string) => void;
  setProgress: (clientMessageId: string, progress: UploadProgress | null) => void;
  setMediaId: (tenantId: string, clientMessageId: string, mediaId: string) => Promise<void>;
}

export const useOutbox = create<OutboxState>((set, get) => ({
  items: [],
  hydratedFor: null,
  hydrate: async (tenantId) => {
    if (get().hydratedFor === tenantId) return;
    const queue = await readQueue(tenantId);
    set({
      hydratedFor: tenantId,
      items: queue.map((q) => ({ ...q, status: "pending" as OutboxStatus, progress: null })),
    });
  },
  add: async (tenantId, item) => {
    set((s) => ({ items: [...s.items, { ...item, status: "pending", progress: null }] }));
    await enqueue(tenantId, item);
  },
  remove: async (tenantId, clientMessageId) => {
    set((s) => ({ items: s.items.filter((i) => i.clientMessageId !== clientMessageId) }));
    releaseLocalMedia(clientMessageId);
    await removeFromQueue(tenantId, clientMessageId);
  },
  setStatus: (clientMessageId, status, errorCode) =>
    set((s) => ({
      items: s.items.map((i) =>
        i.clientMessageId === clientMessageId
          ? { ...i, status, errorCode, progress: status === "sending" ? i.progress : null }
          : i,
      ),
    })),
  setProgress: (clientMessageId, progress) =>
    set((s) => ({
      items: s.items.map((i) => (i.clientMessageId === clientMessageId ? { ...i, progress } : i)),
    })),
  setMediaId: async (tenantId, clientMessageId, mediaId) => {
    set((s) => ({
      items: s.items.map((i) => (i.clientMessageId === clientMessageId ? { ...i, mediaId } : i)),
    }));
    await updateQueued(tenantId, clientMessageId, { mediaId });
  },
}));

export function newClientMessageId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `c_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/**
 * Garante o `mediaId` do item: usa o já gravado (retry pós-upload) ou faz o
 * upload do blob (memória, senão IndexedDB para a fila offline).
 */
export async function ensureMediaId(tenantId: string, item: OutboxItem): Promise<string> {
  if (item.mediaId) return item.mediaId;
  const blob = takeLocalBlob(item.clientMessageId) ?? (await readMediaBlob(item.clientMessageId));
  if (!blob) {
    throw new ZapirApiError("SERVER_ERROR", 0, { missingBlob: true });
  }
  const info = await uploadMedia({
    clientMessageId: item.clientMessageId,
    file: blob,
    fileName: item.mediaName ?? "arquivo",
    conversationId: item.conversationId,
    ...(item.mediaKind ? { kind: item.mediaKind } : {}),
    durationMs: item.mediaDurationMs ?? undefined,
    onProgress: (progress) => useOutbox.getState().setProgress(item.clientMessageId, progress),
  });
  await useOutbox.getState().setMediaId(tenantId, item.clientMessageId, info.id);
  return info.id;
}

/** Drena a fila em ordem; para no primeiro erro nao-recuperavel (RF-UX-051). */
export async function flushOutbox(tenantId: string): Promise<void> {
  const { items, setStatus, remove } = useOutbox.getState();
  const ordered = [...items].sort((a, b) => a.createdAt - b.createdAt);
  for (const item of ordered) {
    if (item.status === "sending") continue;
    setStatus(item.clientMessageId, "sending");
    try {
      if (item.isInternalNote) {
        await api.sendNote({
          conversationId: item.conversationId,
          body: item.body,
          mentions: item.mentions,
        });
      } else if (item.mediaKind) {
        const mediaId = await ensureMediaId(tenantId, item);
        await api.sendMessage({
          conversationId: item.conversationId,
          mediaId,
          caption: item.body || undefined,
          clientMessageId: item.clientMessageId,
          quotedMessageId: item.quotedMessageId,
        });
      } else {
        await api.sendMessage({
          conversationId: item.conversationId,
          body: item.body,
          clientMessageId: item.clientMessageId,
          quotedMessageId: item.quotedMessageId,
        });
      }
      await remove(tenantId, item.clientMessageId);
    } catch (err) {
      const apiError = asApiError(err);
      const network = apiError?.status === 0 && !isRecord(apiError.details);
      setStatus(item.clientMessageId, network ? "pending" : "failed", apiError?.code);
      if (network) {
        // Sem rede: mantem pendente para a proxima reconexao.
        break;
      }
      if (wasAborted(apiError)) {
        setStatus(item.clientMessageId, "pending");
        break;
      }
      toast({
        tone: "danger",
        message: apiError?.copy ?? "Não foi possível enviar agora.",
        actionLabel: "Tentar de novo",
        onAction: () => void flushOutbox(tenantId),
      });
      break;
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function wasAborted(apiError: ZapirApiError | null): boolean {
  return apiError?.status === 0 && isRecord(apiError.details) && apiError.details.aborted === true;
}
